import React from 'react'
import { useNavigate } from 'react-router-dom'
import { DcButton, DcDropDown, DcInput, PixelFactoryContext } from 'pixel-base'
import styled from 'styled-components'
import {
  getAccountingSettings,
  updateAccountingSettings,
} from 'utils/api-routes-accounts'
import FinanceChecklist from '../../components/domain/FinanceChecklist'
import FinanceStatePanel from '../../components/domain/FinanceStatePanel'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceActionBar from '../../components/foundation/FinanceActionBar'
import FinanceCard from '../../components/foundation/FinanceCard'
import FinanceEntityDrawer from '../../components/foundation/FinanceEntityDrawer'
import FinanceInlineAlert from '../../components/foundation/FinanceInlineAlert'
import FinanceSection from '../../components/foundation/FinanceSection'
import { useFinanceDemoMode } from '../../hooks/useFinanceDemoMode'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import {
  AccountingSetting,
  AccountingSetupChecklist,
  FinanceDemoModeStatus,
} from '../../types/finance'

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`

const DEMO_PROFILE_OPTIONS = [
  {
    value: 'simple_1m',
    label: 'Simple 1 Month',
    description:
      'Single-month walkthrough dataset that keeps finance pages and reports non-empty.',
    includes: [
      'One month of periods and baseline ledger activity',
      'Core partners, expenses, invoices, vendor bills, and payments',
      'A lightweight dataset for validation and smoke testing',
    ],
  },
  {
    value: 'smb_6m',
    label: 'SMB 6 Months',
    description:
      'A richer six-month finance dataset for realistic business workflows and reporting.',
    includes: [
      'Six months of operational finance activity',
      'Customers, vendors, allocations, and reconciliation-ready balances',
      'Broader report coverage with more realistic ledger movement',
    ],
  },
] as const

const defaultForm = {
  base_currency_code: 'PKR',
  financial_year_start_month: '1',
  financial_year_start_day: '1',
  default_rounding_precision: '2',
  allow_backdated_posting: 'false',
  backdated_posting_days_limit: '0',
  expense_approval_enabled: 'false',
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '--'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleString()
}

const getReadinessLabel = (demoMode: FinanceDemoModeStatus | null) => {
  if (!demoMode?.is_demo_mode) {
    return 'Live'
  }
  if (
    demoMode.demo_data_status === 'reset' &&
    !demoMode.cleanup_required_before_live_usage
  ) {
    return 'Ready for live usage'
  }
  if (demoMode.cleanup_required_before_live_usage) {
    return 'Cleanup required'
  }
  if (demoMode.demo_data_status === 'installed') {
    return 'Demo data installed'
  }
  return 'Demo mode on'
}

const getDemoMessage = (demoMode: FinanceDemoModeStatus | null) => {
  switch (demoMode?.demo_data_status) {
    case 'installed':
      return 'Demo transactions and balances are active. Reset demo data before actual usage.'
    case 'partially_removed':
      return 'Some demo data cleanup is incomplete. Review the reset result and retry cleanup before starting live usage.'
    case 'reset':
      return 'Demo data has been cleared. You can now start live usage and disable demo mode.'
    case 'live':
      return 'Demo mode has been cleared and this finance workspace is ready for live usage.'
    case 'not_installed':
    default:
      return 'Demo mode is enabled, but no demo data is installed yet. Install demo data to explore the finance module safely.'
  }
}

const getErrorMessage = (error: any) =>
  error?.response?.error || error?.error || error?.message || 'Action failed'

const checklistItems = (
  checklist: AccountingSetupChecklist | null,
  navigate,
) => [
  {
    label: 'Chart of accounts ready',
    complete: !!checklist?.accounts_ready,
    onAction: () => navigate('/finance/accounts'),
  },
  {
    label: 'Journals ready',
    complete: !!checklist?.journals_seeded,
    onAction: () => navigate('/finance/journals'),
  },
  {
    label: 'Bank accounts configured',
    complete: !!checklist?.bank_accounts_configured,
    onAction: () => navigate('/finance/settings/bank-accounts'),
  },
  {
    label: 'First accounting period ready',
    complete: !!checklist?.first_period_ready,
    onAction: () => navigate('/finance/periods'),
  },
  {
    label: 'Opening balances reviewed',
    complete: !!checklist?.opening_balance_ready,
    onAction: () => navigate('/finance/accounts'),
  },
]

const SettingsContent = () => {
  const navigate = useNavigate()
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const [form, setForm] = React.useState<any>(defaultForm)
  const [actionError, setActionError] = React.useState<string>('')
  const [isInstallOpen, setIsInstallOpen] = React.useState(false)
  const [installProfile, setInstallProfile] = React.useState('simple_1m')
  const [installTag, setInstallTag] = React.useState('')
  const loadAccountingSettings = React.useCallback(
    () => getAccountingSettings(),
    [],
  )

  const handleSettingsSuccess = React.useCallback(
    (data: AccountingSetting | undefined) => {
      if (!data) {
        return
      }

      setForm({
        base_currency_code: data.base_currency_code || 'PKR',
        financial_year_start_month: String(
          data.financial_year_start_month || 1,
        ),
        financial_year_start_day: String(data.financial_year_start_day || 1),
        default_rounding_precision: String(
          data.default_rounding_precision || 2,
        ),
        allow_backdated_posting: String(Boolean(data.allow_backdated_posting)),
        backdated_posting_days_limit: String(
          data.backdated_posting_days_limit || 0,
        ),
        expense_approval_enabled: String(
          Boolean(data.expense_approval_enabled),
        ),
      })
    },
    [],
  )

  const settings = useFinanceObservable<AccountingSetting>(
    loadAccountingSettings,
    {
      onSuccess: handleSettingsSuccess,
    },
  )

  const {
    setupStatus,
    demoMode,
    checklist,
    error,
    refresh,
    enableDemoMode,
    installDemoData,
    resetDemoData,
    startLiveUsage,
    disableDemoMode,
  } = useFinanceDemoMode()

  const runAction = async (
    action: () => Promise<any>,
    successMessage: string,
  ) => {
    setActionError('')
    try {
      await action()
      pixelFactory?.showSuccess?.(successMessage)
      refresh()
      settings.refresh()
    } catch (err) {
      const message = getErrorMessage(err)
      setActionError(message)
      pixelFactory?.showFailure?.(message)
    }
  }

  const save = () => {
    updateAccountingSettings({
      ...form,
      financial_year_start_month: Number(form.financial_year_start_month),
      financial_year_start_day: Number(form.financial_year_start_day),
      default_rounding_precision: Number(form.default_rounding_precision),
      allow_backdated_posting: form.allow_backdated_posting === 'true',
      backdated_posting_days_limit: Number(form.backdated_posting_days_limit),
      expense_approval_enabled: form.expense_approval_enabled === 'true',
    }).subscribe({
      next: () => {
        pixelFactory?.showSuccess?.('Accounting settings saved')
        settings.refresh()
        refresh()
      },
      error: (err) => pixelFactory?.showFailure?.(getErrorMessage(err)),
    })
  }

  const readinessLabel = getReadinessLabel(demoMode)
  const currentBatch = demoMode?.last_installed_batch
  const canInstall =
    !!demoMode?.is_demo_mode &&
    (demoMode.demo_data_status !== 'installed' ||
      (demoMode.active_batch_count || 0) === 0)
  const canReset =
    !!demoMode?.is_demo_mode &&
    (demoMode.demo_data_status === 'installed' ||
      demoMode.demo_data_status === 'partially_removed' ||
      !!demoMode.cleanup_required_before_live_usage)
  const canStartLive =
    !!demoMode?.is_demo_mode &&
    demoMode.demo_data_status === 'reset' &&
    !demoMode.cleanup_required_before_live_usage
  const canDisable =
    !!demoMode?.is_demo_mode && !demoMode.cleanup_required_before_live_usage
  const selectedInstallProfile =
    DEMO_PROFILE_OPTIONS.find((profile) => profile.value === installProfile) ||
    DEMO_PROFILE_OPTIONS[0]

  const installSelectedDemoProfile = () =>
    runAction(
      () =>
        installDemoData({
          profile: installProfile,
          tag: installTag || installProfile,
          force_new_batch: false,
        }),
      'Demo data installed. Finance pages are now in demo mode.',
    ).then(() => {
      setIsInstallOpen(false)
      setInstallProfile('simple_1m')
      setInstallTag('')
    })

  return (
    <FinanceSection>
      <FinanceStatePanel
        title="Finance Setup Summary"
        description="Review the current finance setup, demo-state readiness, and the latest demo lifecycle timestamps before moving to live usage."
        status={demoMode?.is_demo_mode ? 'active' : 'inactive'}
        fields={[
          { label: 'Readiness', value: readinessLabel },
          {
            label: 'Demo Data Status',
            value: demoMode?.demo_data_status || 'inactive',
          },
          {
            label: 'Last Demo Install',
            value: formatDate(demoMode?.last_installed_at),
          },
          {
            label: 'Last Demo Reset',
            value: formatDate(demoMode?.last_reset_at),
          },
          {
            label: 'Last Active Batch',
            value: currentBatch
              ? `${currentBatch.profile}${
                  currentBatch.tag ? ` / ${currentBatch.tag}` : ''
                }`
              : '--',
          },
        ]}
      />

      <FinanceCard
        title="Demo Mode"
        description={
          demoMode?.is_demo_mode
            ? getDemoMessage(demoMode)
            : 'Demo mode is currently off. Enable it if you want to load sample finance data for onboarding or validation.'
        }
      >
        <FinanceActionBar>
          {!demoMode?.is_demo_mode && (
            <DcButton
              variant="outline-success"
              onClick={() =>
                runAction(
                  () => enableDemoMode({}),
                  'Demo mode enabled. You can now install demo data from settings.',
                )
              }
            >
              Enable demo mode
            </DcButton>
          )}
          {demoMode?.is_demo_mode && (
            <DcButton
              variant="outline-success"
              disabled={!canInstall}
              onClick={() => setIsInstallOpen(true)}
            >
              Install demo data
            </DcButton>
          )}
          {demoMode?.is_demo_mode && (
            <DcButton
              variant="outline-warning"
              disabled={!canReset}
              onClick={() => {
                if (
                  !window.confirm(
                    'Reset demo data now? This removes demo transactions, partners, cost centers, invoices, bills, payments, and demo-owned balances while preserving finance setup, accounts, journals, periods, and bank accounts.',
                  )
                ) {
                  return
                }
                runAction(
                  () => resetDemoData({ confirmed: true, force: false }),
                  'Demo data cleared. Review your setup, then disable demo mode to start live usage.',
                )
              }}
            >
              Reset demo data
            </DcButton>
          )}
          {demoMode?.is_demo_mode && (
            <DcButton
              variant="outline-secondary"
              disabled={!canStartLive}
              onClick={() => {
                if (
                  !window.confirm(
                    'Start real usage now? This validates go-live readiness. Demo mode still needs to be disabled as the final step.',
                  )
                ) {
                  return
                }
                runAction(
                  () => startLiveUsage({ confirmed: true, force: false }),
                  'Live usage is prepared. Disable demo mode as the final step.',
                )
              }}
            >
              Start real usage
            </DcButton>
          )}
          {demoMode?.is_demo_mode && (
            <DcButton
              variant="outline-danger"
              disabled={!canDisable}
              onClick={() => {
                if (
                  !window.confirm(
                    'Disable demo mode now? This should only be done after demo data cleanup is complete.',
                  )
                ) {
                  return
                }
                runAction(
                  () => disableDemoMode({ confirmed: true }),
                  'Demo mode disabled. Finance is now ready for live usage.',
                )
              }}
            >
              Disable demo mode
            </DcButton>
          )}
        </FinanceActionBar>
        {actionError && (
          <div style={{ marginTop: 14 }}>
            <FinanceInlineAlert tone="danger" message={actionError} />
          </div>
        )}
      </FinanceCard>

      <FinanceCard
        title="Setup Checklist"
        description="Use this checklist before switching to live accounting. Each item links to the area where it can be completed."
      >
        <FinanceChecklist items={checklistItems(checklist, navigate)} />
      </FinanceCard>

      <FinanceCard
        title="General Accounting Settings"
        description="These settings control fiscal year defaults, posting rules, and approval behavior for the finance module."
        actions={<DcButton onClick={save}>Save Settings</DcButton>}
      >
        <Grid>
          <DcInput
            value={form.base_currency_code}
            label="Base Currency"
            onChange={(e) =>
              setForm({ ...form, base_currency_code: e.target.value })
            }
          />
          <DcInput
            value={form.financial_year_start_month}
            label="Financial Year Start Month"
            onChange={(e) =>
              setForm({ ...form, financial_year_start_month: e.target.value })
            }
          />
          <DcInput
            value={form.financial_year_start_day}
            label="Financial Year Start Day"
            onChange={(e) =>
              setForm({ ...form, financial_year_start_day: e.target.value })
            }
          />
          <DcInput
            value={form.default_rounding_precision}
            label="Rounding Precision"
            onChange={(e) =>
              setForm({ ...form, default_rounding_precision: e.target.value })
            }
          />
          <DcDropDown
            value={form.allow_backdated_posting}
            label="Allow Backdated Posting"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            onChange={(e) =>
              setForm({ ...form, allow_backdated_posting: e.target.value })
            }
          />
          <DcInput
            value={form.backdated_posting_days_limit}
            label="Backdated Posting Days Limit"
            onChange={(e) =>
              setForm({ ...form, backdated_posting_days_limit: e.target.value })
            }
          />
          <DcDropDown
            value={form.expense_approval_enabled}
            label="Expense Approval Required"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            onChange={(e) =>
              setForm({ ...form, expense_approval_enabled: e.target.value })
            }
          />
        </Grid>
      </FinanceCard>

      <FinanceCard
        title="Bank Accounts"
        description="Manage finance bank accounts from the settings workspace. This remains the entry point for account configuration."
        actions={
          <DcButton
            variant="outline-primary"
            onClick={() => navigate('/finance/settings/bank-accounts')}
          >
            Open bank accounts
          </DcButton>
        }
      >
        <div />
      </FinanceCard>

      {error && <FinanceInlineAlert tone="danger" message={String(error)} />}
      {setupStatus?.demo_mode?.cleanup_required_before_live_usage && (
        <FinanceInlineAlert
          tone="warning"
          message="Reset demo data before disabling demo mode or starting live usage."
        />
      )}
      <FinanceEntityDrawer
        isOpen={isInstallOpen}
        onClose={() => setIsInstallOpen(false)}
        title="Select Demo Profile"
        width="34%"
        footer={
          <FinanceActionBar align="end">
            <DcButton
              variant="outline-secondary"
              onClick={() => setIsInstallOpen(false)}
            >
              Cancel
            </DcButton>
            <DcButton onClick={installSelectedDemoProfile}>
              Import Batch
            </DcButton>
          </FinanceActionBar>
        }
      >
        <div style={{ display: 'grid', gap: 16 }}>
          <FinanceCard
            title="Choose a demo profile"
            description="Confirm which finance dataset you want to import before continuing."
          >
            <div style={{ display: 'grid', gap: 16 }}>
              <DcDropDown
                label="Profile"
                value={installProfile}
                options={DEMO_PROFILE_OPTIONS.map((profile) => ({
                  value: profile.value,
                  label: profile.label,
                }))}
                onChange={(e) => setInstallProfile(e.target.value)}
              />
              <DcInput
                label="Tag"
                value={installTag}
                placeholder="Optional run label"
                onChange={(e) => setInstallTag(e.target.value)}
              />
            </div>
          </FinanceCard>

          <FinanceCard
            title={selectedInstallProfile.label}
            description={selectedInstallProfile.description}
          >
            <FinanceChecklist
              items={selectedInstallProfile.includes.map((label) => ({
                label,
                complete: true,
              }))}
            />
          </FinanceCard>
        </div>
      </FinanceEntityDrawer>
    </FinanceSection>
  )
}

const Settings = () => {
  return (
    <FinancePageShell title="Accounting Settings">
      <SettingsContent />
    </FinancePageShell>
  )
}

export default Settings
